import { useState, useEffect, useRef } from "react";
import { supabase, KoreanBriefRow } from "../../../lib/supabase";
import ImageUploader from "../../../components/base/ImageUploader";
import BannerUploader, { FocalPoint } from "../../../components/base/BannerUploader";
import ImageCropper from "../../../components/base/ImageCropper";
import RichTextEditor from "../../../components/base/RichTextEditor";
import { useKoreanBriefSettings, KoreanBriefSettings } from "../../../hooks/useKoreanBriefSettings";

type Lang = "vi" | "kr";
const T = (lang: Lang, vi: string, kr: string) => (lang === "vi" ? vi : kr);
type ActiveTab = "widget" | "banner" | "articles";

const DEFAULT_CATEGORIES = [
  "Tổng hợp",
  "Công nghệ",
  "Công nghiệp",
  "Kinh tế",
  "Chính sách",
  "Thị trường",
  "Năng lượng",
  "Y tế",
  "Môi trường",
  "Giáo dục",
  "Thương mại",
  "Tài chính",
  "Vật liệu",
  "Bán dẫn",
];

const emptyArticle = (): Partial<KoreanBriefRow> => ({
  id: "",
  title: "",
  subtitle: "",
  content: "",
  image_url: "",
  gallery: [],
  source_name: "",
  source_url: "",
  category: "general",
  tags: [],
  author: "",
  is_featured: false,
  is_published: true,
  published_at: new Date().toISOString().slice(0, 16),
});

interface Props {
  lang: Lang;
}

// Mock news for widget preview
const PREVIEW_NEWS = [
  { id: "p1", category: "Công nghệ", title: "Hàn Quốc dẫn đầu thế giới về xuất khẩu vật liệu carbon nano" },
  { id: "p2", category: "Kinh tế", title: "Samsung và LG tăng đầu tư vào thị trường Việt Nam năm 2026" },
  { id: "p3", category: "Thương mại", title: "Kim ngạch thương mại Hàn–Việt đạt kỷ lục 87 tỷ USD" },
  { id: "p4", category: "Năng lượng", title: "Hàn Quốc ra mắt pin mặt trời hiệu suất 25%, giá thành giảm 40%" },
  { id: "p5", category: "Chính sách", title: "Hiệp định FTA Hàn–Việt mở rộng thêm 200 mặt hàng miễn thuế" },
];

// ─────────────── Widget Settings Tab ───────────────
const WidgetSettingsTab = ({ lang, settings, saveSettings }: { lang: Lang; settings: KoreanBriefSettings; saveSettings: (s: Partial<KoreanBriefSettings>) => Promise<boolean>; }) => {
  const [local, setLocal] = useState<KoreanBriefSettings>(settings);
  const [logoImages, setLogoImages] = useState<string[]>(settings.widget_logo_url ? [settings.widget_logo_url] : []);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const previewRef = useRef<HTMLDivElement>(null);
  const dragStartRef = useRef<{ mouseX: number; mouseY: number; logoX: number; logoY: number } | null>(null);

  useEffect(() => {
    setLocal(settings);
    setLogoImages(settings.widget_logo_url ? [settings.widget_logo_url] : []);
  }, [settings]);

  const set = (field: keyof KoreanBriefSettings, val: unknown) =>
    setLocal((prev) => ({ ...prev, [field]: val }));

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    dragStartRef.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      logoX: local.widget_logo_x,
      logoY: local.widget_logo_y,
    };
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !previewRef.current || !dragStartRef.current) return;
    const rect = previewRef.current.getBoundingClientRect();
    const dx = e.clientX - dragStartRef.current.mouseX;
    const dy = e.clientY - dragStartRef.current.mouseY;
    const newX = Math.max(0, Math.min(100, dragStartRef.current.logoX + (dx / rect.width) * 100));
    const newY = Math.max(0, Math.min(100, dragStartRef.current.logoY + (dy / rect.height) * 100));
    setLocal((prev) => ({ ...prev, widget_logo_x: Math.round(newX), widget_logo_y: Math.round(newY) }));
  };

  const handleMouseUp = () => { setIsDragging(false); dragStartRef.current = null; };

  const handleSave = async () => {
    setSaving(true);
    const payload = { ...local, widget_logo_url: logoImages[0] ?? "" };
    const ok = await saveSettings(payload);
    setSaving(false);
    if (ok) {
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    }
  };

  const inputCls = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:border-[#004A99] outline-none bg-white";
  const labelCls = "block text-xs font-semibold text-gray-600 mb-1";

  const previewCount = Math.min(local.widget_article_count, PREVIEW_NEWS.length);

  return (
    <div className="space-y-6">
      {/* Info note */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex items-start gap-3">
        <i className="ri-information-line text-blue-500 text-base mt-0.5 flex-shrink-0" />
        <p className="text-xs text-blue-700 leading-relaxed">
          {T(lang,
            "Tab Widget chỉ áp dụng cho Hộp màu xanh ở Trang Chủ. Logo và cài đặt ở đây hoàn toàn độc lập với Tab Banner.",
            "위젯 탭은 홈 페이지의 파란색 박스에만 적용됩니다. 여기의 로고와 설정은 배너 탭과 완전히 독립적입니다."
          )}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Left: Controls */}
        <div className="space-y-5">
          <div>
            <label className={labelCls}>{T(lang, "Tiêu đề Widget", "위젯 제목")}</label>
            <input className={inputCls} value={local.widget_title} onChange={(e) => set("widget_title", e.target.value)} placeholder="Korean Brief" />
            <p className="text-[11px] text-gray-400 mt-1">{T(lang, "Tiêu đề hiển thị trên hộp tin tức ở trang chủ", "홈 페이지 뉴스 박스에 표시되는 제목")}</p>
          </div>

          <div>
            <label className={labelCls}>{T(lang, "Text phụ dưới tiêu đề", "제목 아래 보조 텍스트")}</label>
            <input className={inputCls} value={local.widget_subtitle} onChange={(e) => set("widget_subtitle", e.target.value)} placeholder="Điểm tin Hàn Quốc" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>{T(lang, "Font tiêu đề", "제목 폰트")}</label>
              <select className={inputCls} value={local.widget_font_family} onChange={(e) => set("widget_font_family", e.target.value)}>
                <option value="inherit">Mặc định</option>
                <option value="'Times New Roman', serif">Times New Roman</option>
                <option value="'Be Vietnam Pro', sans-serif">Be Vietnam Pro</option>
                <option value="Arial, sans-serif">Arial</option>
                <option value="'Georgia', serif">Georgia</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>{T(lang, "Cỡ chữ tiêu đề (px)", "제목 크기 (px)")}</label>
              <input type="number" className={inputCls} min={10} max={32} value={local.widget_font_size} onChange={(e) => set("widget_font_size", Number(e.target.value))} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>{T(lang, "Font mô tả", "설명 폰트")}</label>
              <select className={inputCls} value={local.widget_desc_font_family} onChange={(e) => set("widget_desc_font_family", e.target.value)}>
                <option value="inherit">Mặc định</option>
                <option value="'Times New Roman', serif">Times New Roman</option>
                <option value="'Be Vietnam Pro', sans-serif">Be Vietnam Pro</option>
                <option value="Arial, sans-serif">Arial</option>
                <option value="'Georgia', serif">Georgia</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>{T(lang, "Cỡ chữ mô tả (px)", "설명 크기 (px)")}</label>
              <input type="number" className={inputCls} min={9} max={20} value={local.widget_desc_font_size} onChange={(e) => set("widget_desc_font_size", Number(e.target.value))} />
            </div>
          </div>

          <div>
            <label className={labelCls}>{T(lang, "Số bài báo hiển thị", "표시 기사 수")}</label>
            <div className="flex items-center gap-3">
              <input type="range" min={2} max={10} value={local.widget_article_count} onChange={(e) => set("widget_article_count", Number(e.target.value))} className="flex-1 accent-[#004A99]" />
              <span className="text-sm font-bold text-[#004A99] w-6 text-center">{local.widget_article_count}</span>
            </div>
            <p className="text-[11px] text-gray-400 mt-1">{T(lang, "Trang chủ sẽ hiển thị đúng số bài này", "홈 페이지에 표시될 기사 수")}</p>
          </div>

          <ImageUploader
            images={logoImages}
            onChange={(urls) => { setLogoImages(urls); set("widget_logo_url", urls[0] ?? ""); }}
            maxImages={1}
            folder="brief-widget"
            label={T(lang, "Logo Widget", "위젯 로고")}
          />

          {logoImages[0] && (
            <div className="space-y-3">
              <div>
                <label className={labelCls}>{T(lang, "Vị trí X (ngang)", "X 위치 (가로)")}: {local.widget_logo_x}%</label>
                <input type="range" min={0} max={100} value={local.widget_logo_x} onChange={(e) => set("widget_logo_x", Number(e.target.value))} className="w-full accent-[#004A99]" />
              </div>
              <div>
                <label className={labelCls}>{T(lang, "Vị trí Y (dọc)", "Y 위치 (세로)")}: {local.widget_logo_y}%</label>
                <input type="range" min={0} max={100} value={local.widget_logo_y} onChange={(e) => set("widget_logo_y", Number(e.target.value))} className="w-full accent-[#004A99]" />
              </div>
              <div>
                <label className={labelCls}>{T(lang, "Kích thước (Scale)", "크기 (Scale)")}: {local.widget_logo_scale.toFixed(1)}x</label>
                <input type="range" min={0.3} max={3} step={0.1} value={local.widget_logo_scale} onChange={(e) => set("widget_logo_scale", Number(e.target.value))} className="w-full accent-[#004A99]" />
              </div>
            </div>
          )}
        </div>

        {/* Right: Live Preview — exact replica of homepage widget */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <label className={labelCls}>{T(lang, "Xem trước — Kéo thả logo để định vị", "미리보기 — 드래그로 로고 위치 조정")}</label>
            <span className="text-[10px] bg-[#004A99]/10 text-[#004A99] px-2 py-0.5 rounded-full font-semibold">{T(lang, "LIVE", "실시간")}</span>
          </div>
          {/* Widget preview: exact same structure as HeroSection Korean Brief panel */}
          <div
            ref={previewRef}
            className="relative bg-[#001428]/90 border border-white/10 rounded-2xl overflow-hidden select-none"
            style={{ height: 340, cursor: isDragging ? "grabbing" : "default" }}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            {/* Logo - draggable */}
            {logoImages[0] && (
              <div
                className="absolute z-30"
                style={{
                  left: `${local.widget_logo_x}%`,
                  top: `${local.widget_logo_y}%`,
                  transform: `translate(-50%, -50%) scale(${local.widget_logo_scale})`,
                  cursor: isDragging ? "grabbing" : "grab",
                  userSelect: "none",
                }}
                onMouseDown={handleMouseDown}
              >
                <img
                  src={logoImages[0]}
                  alt="Logo preview"
                  className="h-10 w-auto object-contain pointer-events-none"
                  style={{ mixBlendMode: "screen" }}
                  draggable={false}
                />
              </div>
            )}

            {/* Header bar — exact same as homepage */}
            <div className="bg-[#004A99] px-4 py-3 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-newspaper-line text-white text-base" />
                </div>
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <span
                      className="text-white font-bold whitespace-nowrap"
                      style={{
                        fontFamily: local.widget_font_family !== "inherit" ? local.widget_font_family : undefined,
                        fontSize: `${local.widget_font_size ?? 16}px`,
                      }}
                    >
                      {local.widget_title || "Korean Brief"}
                    </span>
                    <span className="bg-[#00AEEF] text-white text-[9px] font-bold px-1.5 py-0.5 rounded">DAILY</span>
                  </div>
                  <span
                    className="text-white/70 text-[10px] whitespace-nowrap tracking-wide"
                    style={{
                      fontFamily: local.widget_desc_font_family !== "inherit" ? local.widget_desc_font_family : undefined,
                    }}
                  >
                    {local.widget_subtitle || "Điểm tin Hàn Quốc"}
                  </span>
                </div>
              </div>
              <span className="text-white/50 text-[10px] whitespace-nowrap">Tiếng Việt</span>
            </div>

            {/* News list — exact same structure as homepage */}
            <div className="flex-1 px-4 py-3 space-y-0 overflow-hidden">
              {PREVIEW_NEWS.slice(0, previewCount).map((news, idx) => (
                <div
                  key={news.id}
                  className="flex gap-2 py-2.5 border-b border-white/5 last:border-0 items-start"
                >
                  <span
                    className="flex-shrink-0 font-bold text-[#00AEEF] bg-[#00AEEF]/10 px-1.5 py-0.5 rounded text-center leading-tight"
                    style={{
                      width: 80,
                      minWidth: 80,
                      maxWidth: 80,
                      fontSize: `${local.widget_desc_font_size ?? 12}px`,
                      fontFamily: local.widget_desc_font_family !== "inherit" ? local.widget_desc_font_family : undefined,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      display: "block",
                    }}
                  >
                    {news.category}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p
                      className="text-white/85 leading-snug line-clamp-2"
                      style={{
                        fontSize: `${local.widget_desc_font_size ?? 13}px`,
                        fontFamily: local.widget_desc_font_family !== "inherit" ? local.widget_desc_font_family : undefined,
                      }}
                    >
                      {news.title}
                    </p>
                  </div>
                  {idx === 0 && <span className="flex-shrink-0 text-[9px] font-black text-white bg-red-500/80 px-1 py-0.5 rounded h-fit whitespace-nowrap">HOT</span>}
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="px-4 py-2.5 border-t border-white/10 flex items-center justify-between">
              <span className="text-white/50 text-[10px]">Tin tức Hàn Quốc mới nhất</span>
              <span className="text-[#00AEEF] text-[10px] font-semibold whitespace-nowrap">Xem tất cả →</span>
            </div>

            {/* Drag hint */}
            {logoImages[0] && (
              <div className="absolute top-[52px] right-2 bg-black/50 text-white text-[9px] px-2 py-0.5 rounded-full pointer-events-none">
                {T(lang, "Kéo logo để di chuyển", "드래그하여 이동")}
              </div>
            )}
          </div>
          <p className="text-[11px] text-gray-400 mt-2">{T(lang, "Preview 1:1 với widget trang chủ. Kéo thả logo hoặc dùng thanh trượt để định vị.", "홈 페이지 위젯과 1:1 미리보기. 드래그 또는 슬라이더로 위치 조정.")}</p>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors ${saved ? "bg-green-600 text-white" : "bg-[#004A99] hover:bg-[#003a7a] text-white"} disabled:opacity-50`}
        >
          {saving ? <i className="ri-loader-4-line animate-spin" /> : saved ? <i className="ri-check-line" /> : <i className="ri-save-line" />}
          {saving ? T(lang, "Đang lưu...", "저장 중...") : saved ? T(lang, "Đã lưu!", "저장됨!") : T(lang, "Lưu cài đặt Widget", "위젯 설정 저장")}
        </button>
      </div>
    </div>
  );
};

// ─────────────── Banner Settings Tab ───────────────
const BannerSettingsTab = ({ lang, settings, saveSettings }: { lang: Lang; settings: KoreanBriefSettings; saveSettings: (s: Partial<KoreanBriefSettings>) => Promise<boolean>; }) => {
  const [local, setLocal] = useState<KoreanBriefSettings>(settings);
  const [coverImages, setCoverImages] = useState<string[]>(settings.banner_cover_url ? [settings.banner_cover_url] : []);
  const [bannerLogoImages, setBannerLogoImages] = useState<string[]>(settings.banner_logo_url ? [settings.banner_logo_url] : []);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [isDraggingLogo, setIsDraggingLogo] = useState(false);
  const previewRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLocal(settings);
    setCoverImages(settings.banner_cover_url ? [settings.banner_cover_url] : []);
    setBannerLogoImages(settings.banner_logo_url ? [settings.banner_logo_url] : []);
  }, [settings]);

  const set = (field: keyof KoreanBriefSettings, val: unknown) =>
    setLocal((prev) => ({ ...prev, [field]: val }));

  const handleLogoMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDraggingLogo(true);
  };

  const handlePreviewMouseMove = (e: React.MouseEvent) => {
    if (!isDraggingLogo || !previewRef.current) return;
    const rect = previewRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
    setLocal((prev) => ({ ...prev, banner_logo_x: Math.round(x), banner_logo_y: Math.round(y) }));
  };

  const handlePreviewMouseUp = () => setIsDraggingLogo(false);

  const handleSave = async () => {
    setSaving(true);
    const payload = {
      ...local,
      banner_cover_url: coverImages[0] ?? "",
      banner_logo_url: bannerLogoImages[0] ?? "",
    };
    const ok = await saveSettings(payload);
    setSaving(false);
    if (ok) {
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    }
  };

  const inputCls = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:border-[#004A99] outline-none bg-white";
  const labelCls = "block text-xs font-semibold text-gray-600 mb-1";

  const highlightedTitle = () => {
    const title = local.banner_title || "";
    const highlight = local.banner_title_highlight || "";
    if (!highlight || !title.includes(highlight)) return <span className="text-white">{title}</span>;
    const idx = title.indexOf(highlight);
    return (
      <>
        <span className="text-white">{title.slice(0, idx)}</span>
        <span className="text-[#00AEEF]">{highlight}</span>
        <span className="text-white">{title.slice(idx + highlight.length)}</span>
      </>
    );
  };

  return (
    <div className="space-y-6">
      {/* Info note */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-3">
        <i className="ri-information-line text-amber-500 text-base mt-0.5 flex-shrink-0" />
        <p className="text-xs text-amber-700 leading-relaxed">
          {T(lang,
            "Tab Banner chỉ áp dụng cho trang danh sách Korean Brief (khi nhấn vào 'Korean Brief' từ menu). Logo và ảnh nền ở đây hoàn toàn độc lập với Tab Widget.",
            "배너 탭은 Korean Brief 목록 페이지에만 적용됩니다. 여기의 로고와 배경 이미지는 위젯 탭과 완전히 독립적입니다."
          )}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Controls */}
        <div className="space-y-4">
          <div>
            <label className={labelCls}>{T(lang, "Slogan phụ (nhỏ)", "서브 슬로건 (소)")}</label>
            <input className={inputCls} value={local.banner_slogan} onChange={(e) => set("banner_slogan", e.target.value)} placeholder="Bizwell" />
          </div>
          <div>
            <label className={labelCls}>{T(lang, "Tiêu đề chính Banner", "배너 메인 제목")}</label>
            <input className={inputCls} value={local.banner_title} onChange={(e) => set("banner_title", e.target.value)} placeholder="Korean Brief Daily" />
          </div>
          <div>
            <label className={labelCls}>{T(lang, "Từ được tô màu trong tiêu đề", "제목에서 색상 강조 단어")}</label>
            <input className={inputCls} value={local.banner_title_highlight} onChange={(e) => set("banner_title_highlight", e.target.value)} placeholder="Daily" />
            <p className="text-[11px] text-gray-400 mt-1">{T(lang, "Từ này sẽ được tô màu xanh trong tiêu đề", "이 단어는 제목에서 파란색으로 강조됩니다")}</p>
          </div>
          <div>
            <label className={labelCls}>{T(lang, "Mô tả ngắn Banner", "배너 짧은 설명")}</label>
            <textarea
              className={`${inputCls} resize-none`}
              rows={3}
              value={local.banner_description}
              onChange={(e) => set("banner_description", e.target.value)}
              placeholder="Tin tức kinh tế & công nghệ Hàn Quốc..."
            />
          </div>

          {/* Banner cover image */}
          <ImageUploader
            images={coverImages}
            onChange={(urls) => { setCoverImages(urls); set("banner_cover_url", urls[0] ?? ""); }}
            maxImages={1}
            folder="brief-banner"
            label={T(lang, "Ảnh nền Banner (Cover Image)", "배너 배경 이미지")}
          />

          {/* Banner logo - separate from widget logo */}
          <div className="border-t border-gray-100 pt-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-5 h-5 rounded bg-[#004A99]/10 flex items-center justify-center">
                <i className="ri-image-line text-[#004A99] text-xs" />
              </div>
              <span className="text-xs font-bold text-gray-700">{T(lang, "Logo riêng cho trang Banner", "배너 페이지 전용 로고")}</span>
              <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-semibold">{T(lang, "Độc lập với Widget", "위젯과 독립")}</span>
            </div>
            <ImageUploader
              images={bannerLogoImages}
              onChange={(urls) => { setBannerLogoImages(urls); set("banner_logo_url", urls[0] ?? ""); }}
              maxImages={1}
              folder="brief-banner-logo"
              label={T(lang, "Upload logo cho trang Banner", "배너 페이지 로고 업로드")}
            />
            {bannerLogoImages[0] && (
              <div className="mt-3 space-y-2">
                <div>
                  <label className={labelCls}>{T(lang, "Vị trí X", "X 위치")}: {local.banner_logo_x}%</label>
                  <input type="range" min={0} max={100} value={local.banner_logo_x} onChange={(e) => set("banner_logo_x", Number(e.target.value))} className="w-full accent-[#004A99]" />
                </div>
                <div>
                  <label className={labelCls}>{T(lang, "Vị trí Y", "Y 위치")}: {local.banner_logo_y}%</label>
                  <input type="range" min={0} max={100} value={local.banner_logo_y} onChange={(e) => set("banner_logo_y", Number(e.target.value))} className="w-full accent-[#004A99]" />
                </div>
                <div>
                  <label className={labelCls}>{T(lang, "Kích thước", "크기")}: {local.banner_logo_scale.toFixed(1)}x</label>
                  <input type="range" min={0.3} max={3} step={0.1} value={local.banner_logo_scale} onChange={(e) => set("banner_logo_scale", Number(e.target.value))} className="w-full accent-[#004A99]" />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Live Preview - mimics the actual Korean Brief page banner */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <label className={labelCls}>{T(lang, "Xem trước — Trang Korean Brief (Hình 1)", "미리보기 — Korean Brief 목록 페이지")}</label>
            <span className="text-[10px] bg-[#004A99]/10 text-[#004A99] px-2 py-0.5 rounded-full font-semibold">{T(lang, "LIVE", "실시간")}</span>
          </div>
          <div
            ref={previewRef}
            className="relative rounded-xl overflow-hidden select-none cursor-crosshair"
            style={{ height: 260 }}
            onMouseMove={handlePreviewMouseMove}
            onMouseUp={handlePreviewMouseUp}
            onMouseLeave={handlePreviewMouseUp}
          >
            {coverImages[0] ? (
              <img src={coverImages[0]} alt="Banner preview" className="absolute inset-0 w-full h-full object-cover object-top" />
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-[#001428] via-[#004A99] to-[#002d5f]" />
            )}
            <div className="absolute inset-0 bg-black/40" />

            {/* Decorative circles like real page */}
            <div className="absolute top-0 right-0 w-32 h-32 rounded-full bg-[#00AEEF]/10 -translate-y-1/2 translate-x-1/4" />
            <div className="absolute bottom-0 left-0 w-20 h-20 rounded-full bg-[#004A99]/10 translate-y-1/2 -translate-x-1/4" />

            <div className="absolute inset-0 flex flex-col justify-center px-6">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-6 h-[1px] bg-[#00AEEF]" />
                <span className="text-[#00AEEF] text-[10px] font-bold uppercase tracking-widest">{local.banner_slogan || "Bizwell"}</span>
              </div>
              <h1 className="text-white font-black text-2xl mb-2 leading-tight">
                {highlightedTitle()}
              </h1>
              <p className="text-white/70 text-xs leading-relaxed max-w-xs">
                {local.banner_description || T(lang, "Mô tả ngắn sẽ hiển thị ở đây...", "짧은 설명이 여기 표시됩니다...")}
              </p>
              {/* Search bar preview */}
              <div className="mt-3 flex items-center bg-white/20 rounded-lg px-3 py-1.5 max-w-[200px]">
                <i className="ri-search-line text-white/60 text-xs mr-2" />
                <span className="text-white/50 text-[10px]">{T(lang, "Tìm kiếm...", "검색...")}</span>
              </div>
            </div>

            {/* Banner logo - draggable */}
            {bannerLogoImages[0] && (
              <div
                className="absolute cursor-grab active:cursor-grabbing"
                style={{
                  left: `${local.banner_logo_x}%`,
                  top: `${local.banner_logo_y}%`,
                  transform: `translate(-50%, -50%) scale(${local.banner_logo_scale})`,
                  transformOrigin: "center",
                }}
                onMouseDown={handleLogoMouseDown}
              >
                <img
                  src={bannerLogoImages[0]}
                  alt="Banner logo preview"
                  className="h-12 w-auto object-contain pointer-events-none"
                  style={{ mixBlendMode: "screen" }}
                  draggable={false}
                />
              </div>
            )}

            <div className="absolute top-2 right-2 bg-green-500/90 text-white text-[9px] px-2 py-0.5 rounded-full font-bold">
              {T(lang, "LIVE PREVIEW", "실시간 미리보기")}
            </div>
          </div>
          <p className="text-[11px] text-gray-400 mt-2">{T(lang, "Kéo thả logo trong ô xem trước để định vị. Thay đổi cập nhật ngay sau khi Lưu.", "미리보기에서 로고를 드래그하여 위치 조정. 저장 후 즉시 반영됩니다.")}</p>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors ${saved ? "bg-green-600 text-white" : "bg-[#004A99] hover:bg-[#003a7a] text-white"} disabled:opacity-50`}
        >
          {saving ? <i className="ri-loader-4-line animate-spin" /> : saved ? <i className="ri-check-line" /> : <i className="ri-save-line" />}
          {saving ? T(lang, "Đang lưu...", "저장 중...") : saved ? T(lang, "Đã lưu! Website đã cập nhật", "저장됨! 웹사이트 업데이트됨") : T(lang, "Lưu cài đặt Banner", "배너 설정 저장")}
        </button>
      </div>
    </div>
  );
};

// ─────────────── Articles Tab (existing) ───────────────
const ArticlesTab = ({ lang }: { lang: Lang }) => {
  const [articles, setArticles] = useState<KoreanBriefRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Partial<KoreanBriefRow> | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [coverImages, setCoverImages] = useState<string[]>([]);
  const [coverFocal, setCoverFocal] = useState<FocalPoint>({ x: 50, y: 50 });
  const [galleryImages, setGalleryImages] = useState<string[]>([]);
  const [previewMode, setPreviewMode] = useState(false);
  // Dynamic categories (combo box)
  const [dynamicCategories, setDynamicCategories] = useState<string[]>(DEFAULT_CATEGORIES);
  const [categoryInput, setCategoryInput] = useState("");
  const [showCatDropdown, setShowCatDropdown] = useState(false);

  const fetchArticles = async () => {
    setLoading(true);
    const { data } = await supabase.from("korean_brief").select("*").order("published_at", { ascending: false });
    const rows = (data as KoreanBriefRow[]) ?? [];
    setArticles(rows);
    // Collect unique categories from DB to enrich combo box
    const dbCats = rows.map((a) => a.category).filter(Boolean) as string[];
    setDynamicCategories((prev) => {
      const merged = [...new Set([...prev, ...dbCats])];
      return merged;
    });
    setLoading(false);
  };

  useEffect(() => { fetchArticles(); }, []);

  const openNew = () => { setEditing(emptyArticle()); setCoverImages([]); setCoverFocal({ x: 50, y: 50 }); setGalleryImages([]); setTagInput(""); setCategoryInput(""); setError(""); setPreviewMode(false); };
  const openEdit = (a: KoreanBriefRow) => { setEditing({ ...a }); setCoverImages(a.image_url ? [a.image_url] : []); setCoverFocal({ x: 50, y: 50 }); setGalleryImages(a.gallery ?? []); setTagInput(""); setCategoryInput(a.category ?? ""); setError(""); setPreviewMode(false); };
  const set = (field: keyof KoreanBriefRow, val: unknown) => setEditing((prev) => prev ? { ...prev, [field]: val } : prev);
  const addTag = () => { if (!tagInput.trim()) return; set("tags", [...((editing?.tags ?? []) as string[]), tagInput.trim()]); setTagInput(""); };
  const removeTag = (i: number) => { const t = [...((editing?.tags ?? []) as string[])]; t.splice(i, 1); set("tags", t); };

  const handleSave = async () => {
    if (!editing.title?.trim()) { setError(T(lang, "Vui lòng nhập tiêu đề bài!", "제목을 입력하세요!")); return; }
    if (!editing.id?.trim()) {
      const autoId = `brief-${Date.now()}`;
      setEditing(prev => prev ? { ...prev, id: autoId } : prev);
      editing.id = autoId;
    }
    setSaving(true); setError("");
    // Auto-add new category to dynamic list
    const finalCategory = categoryInput.trim() || editing.category || "Tổng hợp";
    if (finalCategory && !dynamicCategories.includes(finalCategory)) {
      setDynamicCategories((prev) => [...prev, finalCategory]);
    }
    const payload: KoreanBriefRow = { ...(editing as KoreanBriefRow), category: finalCategory, image_url: coverImages[0] ?? editing.image_url ?? "", gallery: galleryImages, updated_at: new Date().toISOString() };
    const { error: dbErr } = await supabase.from("korean_brief").upsert(payload, { onConflict: "id" });
    setSaving(false);
    if (dbErr) { setError(dbErr.message); return; }
    setEditing(null);
    fetchArticles();
  };

  const handleDelete = async (id: string) => { await supabase.from("korean_brief").delete().eq("id", id); setDeleteConfirm(null); fetchArticles(); };

  const inputCls = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:border-[#004A99] outline-none bg-white";
  const labelCls = "block text-xs font-semibold text-gray-600 mb-1";

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-gray-500">{T(lang, "Đăng bài báo, tin tức công nghệ Hàn Quốc", "한국 기술 뉴스 및 기사 게시")}</p>
        <button onClick={openNew} className="flex items-center gap-2 px-4 py-2 bg-[#004A99] text-white text-sm font-semibold rounded-xl hover:bg-[#003a7a] cursor-pointer whitespace-nowrap transition-colors">
          <i className="ri-quill-pen-line" />
          {T(lang, "Đăng Bài Mới", "새 기사 게시")}
        </button>
      </div>

      {loading ? (
        <div className="text-center py-20"><i className="ri-loader-4-line text-2xl text-[#004A99] animate-spin" /></div>
      ) : articles.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-gray-200">
          <i className="ri-newspaper-line text-4xl text-gray-200 block mb-4" />
          <p className="text-gray-500 text-sm">{T(lang, "Chưa có bài nào. Đăng bài đầu tiên!", "기사가 없습니다.")}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {articles.map((a) => (
            <div key={a.id} className="bg-white rounded-2xl border border-gray-200 p-5 flex gap-4">
              <div className="w-28 h-20 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0">
                {a.image_url ? <img src={a.image_url} alt={a.title} className="w-full h-full object-cover object-top" /> : <div className="w-full h-full flex items-center justify-center"><i className="ri-newspaper-line text-gray-300 text-2xl" /></div>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] bg-[#EDF2FB] text-[#004A99] px-2 py-0.5 rounded-full font-semibold whitespace-nowrap">{a.category}</span>
                  {a.is_featured && <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-semibold whitespace-nowrap">{T(lang, "Nổi bật", "주요")}</span>}
                  {!a.is_published && <span className="text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-semibold whitespace-nowrap">{T(lang, "Nháp", "초안")}</span>}
                </div>
                <h3 className="font-bold text-gray-900 text-sm line-clamp-1 mb-1">{a.title}</h3>
                <p className="text-xs text-gray-500 line-clamp-1 mb-2">{a.subtitle}</p>
                <div className="flex items-center gap-3 text-[10px] text-gray-400">
                  {a.source_name && <span><i className="ri-links-line mr-1" />{a.source_name}</span>}
                  <span><i className="ri-calendar-line mr-1" />{a.published_at?.slice(0, 10)}</span>
                  {a.author && <span><i className="ri-user-line mr-1" />{a.author}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button onClick={() => openEdit(a)} className="p-2 hover:bg-[#EDF2FB] rounded-lg cursor-pointer transition-colors"><i className="ri-edit-line text-[#004A99]" /></button>
                <button onClick={() => setDeleteConfirm(a.id)} className="p-2 hover:bg-red-50 rounded-lg cursor-pointer transition-colors"><i className="ri-delete-bin-line text-red-400" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4"><i className="ri-delete-bin-line text-red-500 text-xl" /></div>
            <h3 className="font-bold text-gray-900 text-center mb-2">{T(lang, "Xóa bài báo này?", "이 기사를 삭제할까요?")}</h3>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm cursor-pointer whitespace-nowrap hover:bg-gray-50">{T(lang, "Hủy", "취소")}</button>
              <button onClick={() => handleDelete(deleteConfirm)} className="flex-1 py-2.5 bg-red-500 text-white rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap hover:bg-red-600">{T(lang, "Xóa", "삭제")}</button>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[95vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between z-10">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-[#004A99] flex items-center justify-center">
                  <i className="ri-newspaper-line text-white text-base" />
                </div>
                <div>
                  <h2 className="font-bold text-gray-800 text-sm">{T(lang, "Đăng bài Korean Brief", "Korean Brief 게시")}</h2>
                  <p className="text-[11px] text-gray-400">{T(lang, "Biên tập & xuất bản bài báo", "기사 편집 및 게시")}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setPreviewMode(!previewMode)} className={`px-3 py-1.5 text-xs rounded-lg font-medium cursor-pointer whitespace-nowrap transition-colors ${previewMode ? "bg-[#004A99] text-white" : "bg-gray-100 text-gray-600"}`}>
                  <i className={`${previewMode ? "ri-edit-line" : "ri-eye-line"} mr-1`} />
                  {previewMode ? T(lang, "Chỉnh sửa", "수정") : T(lang, "Xem trước", "미리보기")}
                </button>
                <button onClick={() => setEditing(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer"><i className="ri-close-line text-gray-500" /></button>
              </div>
            </div>

            {previewMode ? (
              <div className="p-6">
                <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                  {coverImages[0] && <img src={coverImages[0]} alt={editing.title ?? ""} className="w-full h-64 object-cover object-top" />}
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-[10px] bg-[#EDF2FB] text-[#004A99] px-2 py-0.5 rounded-full font-semibold">{editing.category}</span>
                      {editing.is_featured && <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-semibold">{T(lang, "Nổi bật", "주요")}</span>}
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">{editing.title ?? T(lang, "Tiêu đề bài", "제목")}</h1>
                    <p className="text-gray-500 mb-4">{editing.subtitle}</p>
                    <div className="flex items-center gap-3 text-xs text-gray-400 mb-4">
                      {editing.author && <span><i className="ri-user-line mr-1" />{editing.author}</span>}
                      {editing.source_name && (
                        <a href={editing.source_url ?? "#"} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[#004A99] hover:underline">
                          <i className="ri-external-link-line" />{editing.source_name}
                        </a>
                      )}
                    </div>
                    <div className="prose prose-sm text-gray-700 leading-relaxed rich-editor-content" dangerouslySetInnerHTML={{ __html: editing.content ?? "" }} />
                    {galleryImages.length > 0 && (
                      <div className="grid grid-cols-2 gap-3 mt-6">{galleryImages.map((img, i) => <img key={i} src={img} alt="" className="w-full h-40 object-cover rounded-xl" />)}</div>
                    )}
                    {(editing.tags as string[] ?? []).length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-4">{(editing.tags as string[]).map((tag, i) => <span key={i} className="text-xs bg-gray-100 text-gray-600 px-3 py-1 rounded-full">#{tag}</span>)}</div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-6 space-y-4">
                {error && <div className="bg-red-50 text-red-700 text-xs rounded-lg px-3 py-2 border border-red-200">{error}</div>}
                <div className="grid grid-cols-3 gap-4">
                  <div className="hidden">
                    <label className={labelCls}>ID bài * (slug)</label>
                    <input className={inputCls} placeholder="carbon-nano-news-2026" value={editing.id ?? ""} onChange={(e) => set("id", e.target.value)} disabled={!!articles.find(a => a.id === editing.id)} />
                  </div>
                  <div className="relative">
                    <label className={labelCls}>{T(lang, "Chủ đề (gõ mới hoặc chọn)", "카테고리")}</label>
                    <input
                      className={inputCls}
                      placeholder={T(lang, "Gõ hoặc chọn chủ đề...", "카테고리 입력 또는 선택...")}
                      value={categoryInput}
                      onChange={(e) => { setCategoryInput(e.target.value); set("category", e.target.value); setShowCatDropdown(true); }}
                      onFocus={() => setShowCatDropdown(true)}
                      onBlur={() => setTimeout(() => setShowCatDropdown(false), 150)}
                    />
                    {showCatDropdown && (
                      <div className="absolute z-50 top-full left-0 right-0 bg-white border border-gray-200 rounded-lg mt-1 max-h-40 overflow-y-auto">
                        {dynamicCategories.filter(c => !categoryInput || c.toLowerCase().includes(categoryInput.toLowerCase())).map(c => (
                          <button key={c} type="button" onMouseDown={() => { setCategoryInput(c); set("category", c); setShowCatDropdown(false); }}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-[#EDF2FB] cursor-pointer whitespace-nowrap">
                            {c}
                          </button>
                        ))}
                        {categoryInput && !dynamicCategories.includes(categoryInput) && (
                          <button type="button" onMouseDown={() => { setShowCatDropdown(false); }}
                            className="w-full text-left px-3 py-2 text-sm text-[#004A99] font-semibold hover:bg-[#EDF2FB] cursor-pointer whitespace-nowrap">
                            + Thêm mới: "{categoryInput}"
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className={labelCls}>{T(lang, "Tác giả", "작성자")}</label>
                    <input className={inputCls} value={editing.author ?? ""} onChange={(e) => set("author", e.target.value)} />
                  </div>
                </div>
                <div>
                  <label className={labelCls}>{T(lang, "Tiêu đề bài *", "제목 *")}</label>
                  <input className={inputCls} placeholder={T(lang, "Nhập tiêu đề hấp dẫn...", "매력적인 제목 입력...")} value={editing.title ?? ""} onChange={(e) => set("title", e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>{T(lang, "Tóm tắt / Subtitle", "부제목")}</label>
                  <input className={inputCls} placeholder={T(lang, "Mô tả ngắn về bài...", "간략한 설명...")} value={editing.subtitle ?? ""} onChange={(e) => set("subtitle", e.target.value)} />
                </div>
                <RichTextEditor value={editing.content ?? ""} onChange={(html) => set("content", html)} placeholder={T(lang, "Nhập nội dung bài báo đầy đủ tại đây...", "기사 내용을 여기에 입력하세요...")} minHeight={320} folder="brief" label={T(lang, "Nội dung bài báo", "기사 내용")} />
                <ImageCropper
                  imageUrl={coverImages[0] ?? ""}
                  onChange={(url) => { setCoverImages(url ? [url] : []); set("image_url", url); }}
                  folder="brief"
                  label={T(lang, "Ảnh bìa bài báo (Hỗ trợ cắt 16:9 / 4:3 / 1:1, zoom, xem trước realtime)", "커버 이미지 (16:9 / 4:3 / 1:1 크롭, 줌, 실시간 미리보기)")}
                  defaultAspect="16:9"
                />
                <ImageUploader images={galleryImages} onChange={(urls) => { setGalleryImages(urls); set("gallery", urls); }} maxImages={8} folder="brief-gallery" label={T(lang, "Bộ ảnh minh họa (tùy chọn)", "추가 이미지 (선택)")} />
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>{T(lang, "Tên nguồn báo", "출처 이름")}</label>
                    <input className={inputCls} placeholder="Korea Economic Daily" value={editing.source_name ?? ""} onChange={(e) => set("source_name", e.target.value)} />
                  </div>
                  <div>
                    <label className={labelCls}>{T(lang, "Link bài báo gốc", "원문 링크")}</label>
                    <input className={inputCls} placeholder="https://..." value={editing.source_url ?? ""} onChange={(e) => set("source_url", e.target.value)} />
                  </div>
                </div>
                <div>
                  <label className={labelCls}>{T(lang, "Ngày xuất bản", "게시 날짜")}</label>
                  <input type="datetime-local" className={inputCls} value={(editing.published_at ?? "").slice(0, 16)} onChange={(e) => set("published_at", e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>{T(lang, "Tags / Thẻ", "태그")}</label>
                  <div className="flex gap-2 mb-2">
                    <input className={`${inputCls} flex-1`} placeholder={T(lang, "Nhập tag rồi bấm Thêm...", "태그 입력 후 추가...")} value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); }}} />
                    <button type="button" onClick={addTag} className="px-3 py-2 bg-[#004A99] text-white rounded-lg text-sm cursor-pointer whitespace-nowrap">{T(lang, "Thêm", "추가")}</button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {((editing.tags ?? []) as string[]).map((tag, i) => (
                      <span key={i} className="inline-flex items-center gap-1.5 bg-[#EDF2FB] text-[#004A99] text-xs px-3 py-1.5 rounded-full">
                        #{tag}
                        <button onClick={() => removeTag(i)} className="cursor-pointer hover:text-red-500 transition-colors"><i className="ri-close-line text-xs" /></button>
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={editing.is_featured ?? false} onChange={(e) => set("is_featured", e.target.checked)} className="w-4 h-4 rounded" />
                    <span className="text-sm text-gray-700">{T(lang, "Bài nổi bật", "주요 기사")}</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={editing.is_published ?? true} onChange={(e) => set("is_published", e.target.checked)} className="w-4 h-4 rounded" />
                    <span className="text-sm text-gray-700">{T(lang, "Xuất bản ngay", "즉시 게시")}</span>
                  </label>
                </div>
              </div>
            )}

            <div className="sticky bottom-0 bg-white border-t border-gray-100 px-6 py-4 flex items-center justify-between">
              <button onClick={() => setPreviewMode(!previewMode)} className="text-sm text-[#004A99] font-medium cursor-pointer">
                <i className={`${previewMode ? "ri-edit-line" : "ri-eye-line"} mr-1`} />
                {previewMode ? T(lang, "Tiếp tục chỉnh sửa", "계속 수정") : T(lang, "Xem trước bài", "미리보기")}
              </button>
              <div className="flex gap-3">
                <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer whitespace-nowrap">{T(lang, "Hủy", "취소")}</button>
                <button onClick={handleSave} disabled={saving} className="px-5 py-2 bg-[#004A99] hover:bg-[#003a7a] text-white text-sm font-semibold rounded-lg cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center gap-2">
                  {saving && <i className="ri-loader-4-line animate-spin" />}
                  <i className="ri-send-plane-line" />
                  {T(lang, "Đăng Bài", "게시하기")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────── Main Manager ───────────────
const KoreanBriefManager = ({ lang }: Props) => {
  const [activeTab, setActiveTab] = useState<ActiveTab>("articles");
  const { settings, loading: settingsLoading, saveSettings } = useKoreanBriefSettings();

  const tabs: { id: ActiveTab; label: string; labelKr: string; icon: string; desc: string; descKr: string }[] = [
    { id: "widget", label: "Tab Widget", labelKr: "위젯 탭", icon: "ri-layout-3-line", desc: "Tiêu đề, logo, vị trí logo, số bài", descKr: "제목, 로고, 위치, 기사 수" },
    { id: "banner", label: "Tab Banner", labelKr: "배너 탭", icon: "ri-image-2-line", desc: "Ảnh nền, tiêu đề, mô tả trang chi tiết", descKr: "배경 이미지, 제목, 설명" },
    { id: "articles", label: "Tab Tin tức", labelKr: "기사 탭", icon: "ri-newspaper-line", desc: "Danh sách và quản lý bài báo", descKr: "기사 목록 및 관리" },
  ];

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-800">{T(lang, "Quản Lý Korean Brief", "Korean Brief 관리")}</h1>
        <p className="text-sm text-gray-500 mt-1">{T(lang, "Tùy biến widget, banner trang chi tiết và quản lý bài viết", "위젯, 배너 및 기사 관리")}</p>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-3 mb-6">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 flex flex-col items-start gap-1 p-4 rounded-xl border-2 transition-all cursor-pointer text-left ${
              activeTab === tab.id
                ? "border-[#004A99] bg-[#EDF2FB]"
                : "border-gray-200 bg-white hover:border-[#004A99]/30"
            }`}
          >
            <div className="flex items-center gap-2">
              <div className={`w-7 h-7 flex items-center justify-center rounded-lg ${activeTab === tab.id ? "bg-[#004A99]" : "bg-gray-100"}`}>
                <i className={`${tab.icon} text-sm ${activeTab === tab.id ? "text-white" : "text-gray-500"}`} />
              </div>
              <span className={`text-sm font-bold ${activeTab === tab.id ? "text-[#004A99]" : "text-gray-700"}`}>
                {lang === "vi" ? tab.label : tab.labelKr}
              </span>
            </div>
            <p className="text-[11px] text-gray-400 pl-9">{lang === "vi" ? tab.desc : tab.descKr}</p>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        {settingsLoading && (activeTab === "widget" || activeTab === "banner") ? (
          <div className="text-center py-16"><i className="ri-loader-4-line text-2xl text-[#004A99] animate-spin" /></div>
        ) : (
          <>
            {activeTab === "widget" && <WidgetSettingsTab lang={lang} settings={settings} saveSettings={saveSettings} />}
            {activeTab === "banner" && <BannerSettingsTab lang={lang} settings={settings} saveSettings={saveSettings} />}
            {activeTab === "articles" && <ArticlesTab lang={lang} />}
          </>
        )}
      </div>
    </div>
  );
};

export default KoreanBriefManager;
